import { createAsyncThunk, createSlice } from "@reduxjs/toolkit"
import { differenceWith } from "lodash-es"
import * as API from "services/api"
import { errorToast } from "shared/toast"

const SLICE_NAME = "taskReportBuilder"

// Matches column type strings used on BE (ExportInstructions::DefaultColumns)
export const SUBMISSION_TYPE = "submission"
export const TASK_TYPE = "task"
export const TASK_QUESTION_TYPE = "task_question"

export const UNINITIALIZED = "uninitialized"
export const LOADING = "loading"
export const LOADED = "loaded"

const INITIAL_STATE = {
  name: "",
  shared: false,
  categories: [],
  selectedCategorySlug: null,
  taskForms: [],
  selectedTaskFormSlug: null,
  formQuestions: [],
  submissionColumnOptions: [],
  taskColumnOptions: [],
  orderedColumns: [],
  status: UNINITIALIZED,
}

const columnsMatch = (col1, col2) => (col1.type === col2.type && col1.id === col2.id)

const mapFormQuestions = (questions) => questions.map(({ uuid, prompt, removedAt }) => (
  { id: uuid, label: `${prompt}${removedAt ? " (removed)" : ""}`, type: TASK_QUESTION_TYPE }
))

export const selectCategory = createAsyncThunk(
  `${SLICE_NAME}/selectCategory`,
  async ({ categorySlug }, thunkAPI) => {
    const currentState = thunkAPI.getState()[SLICE_NAME]

    if (categorySlug === currentState.selectedCategorySlug) return currentState

    if (!categorySlug) {
      return {
        selectedCategorySlug: null,
        taskForms: [],
        selectedTaskFormSlug: null,
        formQuestions: [],
      }
    }

    const response = await API.getCategorySubforms({ categorySlug })

    if (!response.ok) {
      throw new Error("Something went wrong. Unable to load task forms.")
    }

    return {
      selectedCategorySlug: categorySlug,
      taskForms: response.data,
      selectedTaskFormSlug: null,
      formQuestions: [],
    }
  },
)

export const selectTaskForm = createAsyncThunk(
  `${SLICE_NAME}/selectTaskForm`,
  async ({ taskFormSlug }) => {
    const updatedData = { selectedTaskFormSlug: taskFormSlug, formQuestions: [] }

    if (taskFormSlug) {
      const response = await API.formQuestionHistory({ formSlug: taskFormSlug })

      if (!response.ok) {
        throw new Error("Something went wrong. Unable to load task form questions.")
      }

      updatedData.formQuestions = mapFormQuestions(response.data)
    }

    return updatedData
  },
)

export const loadTaskReportDataAsync = createAsyncThunk(
  `${SLICE_NAME}/loadTaskReportDataAsync`,
  async ({ reportData }, thunkAPI) => {
    try {
      const { selectedCategorySlug: categorySlug, selectedTaskFormSlug: taskFormSlug } = reportData

      let taskForms = []
      let formQuestions = []

      if (categorySlug) {
        const categoryResponse = await API.getCategorySubforms({ categorySlug })

        if (!categoryResponse.ok) {
          throw new Error("Something went wrong. Unable to load task forms.")
        }

        taskForms = categoryResponse.data
      }

      if (taskFormSlug) {
        const questionsResponse = await API.formQuestionHistory({ formSlug: taskFormSlug })

        if (!questionsResponse.ok) {
          throw new Error("Something went wrong. Unable to load task form questions.")
        }

        formQuestions = questionsResponse.data
      }

      return { ...reportData, taskForms, formQuestions }
    } catch (error) {
      throw thunkAPI.rejectWithValue(error.message)
    }
  },
)

const buildLabelMap = (options) => options.reduce((map, option) => {
  map[option.id] = option.label
  return map
}, {})

// Submitted orderedColumns only carry {id, type} - reattach labels from the
// current option sets (submission/task defaults) or the selected task form's questions.
const mapOrderedColumnLabels = (state, payload) => {
  state.taskForms = payload.taskForms
  state.formQuestions = mapFormQuestions(payload.formQuestions)

  const labelsByType = {
    [SUBMISSION_TYPE]: buildLabelMap(state.submissionColumnOptions),
    [TASK_TYPE]: buildLabelMap(state.taskColumnOptions),
    [TASK_QUESTION_TYPE]: buildLabelMap(state.formQuestions),
  }

  state.orderedColumns = payload.orderedColumns.reduce((result, column) => {
    const label = (labelsByType[column.type] ?? {})[column.id]

    if (label) result.push({ ...column, label })

    return result
  }, [])
}

export const taskReportBuilderSlice = createSlice({
  name: SLICE_NAME,
  initialState: INITIAL_STATE,
  reducers: {
    deselectAllQuestionColumns: (state) => {
      state.orderedColumns = state.orderedColumns.filter(({ type }) => type !== TASK_QUESTION_TYPE)
    },
    deselectAllSubmissionDefaultColumns: (state) => {
      state.orderedColumns = state.orderedColumns.filter(({ type }) => type !== SUBMISSION_TYPE)
    },
    deselectAllTaskDefaultColumns: (state) => {
      state.orderedColumns = state.orderedColumns.filter(({ type }) => type !== TASK_TYPE)
    },
    deselectColumn: (state, { payload }) => {
      const { id, type } = payload
      state.orderedColumns = state.orderedColumns.filter((col) => !(col.id === id && col.type === type))
    },
    moveColumn: (state, { payload }) => {
      const { column: movedColumn, toIndex } = payload
      const newColumns = [...state.orderedColumns]
      const currentIndex = state.orderedColumns.findIndex(
        (col) => col.id === movedColumn.id && col.type === movedColumn.type,
      )

      if (currentIndex === -1) {
        newColumns.splice(toIndex, 0, movedColumn)
      } else {
        const [column] = newColumns.splice(currentIndex, 1)
        newColumns.splice(toIndex, 0, column)
      }

      state.orderedColumns = newColumns
    },
    selectAllQuestionColumns: (state) => {
      const unselected = differenceWith(state.formQuestions, state.orderedColumns, columnsMatch)
      state.orderedColumns = state.orderedColumns.concat(unselected)
    },
    selectAllSubmissionDefaultColumns: (state) => {
      const unselected = differenceWith(state.submissionColumnOptions, state.orderedColumns, columnsMatch)
      state.orderedColumns = state.orderedColumns.concat(unselected)
    },
    selectAllTaskDefaultColumns: (state) => {
      const unselected = differenceWith(state.taskColumnOptions, state.orderedColumns, columnsMatch)
      state.orderedColumns = state.orderedColumns.concat(unselected)
    },
    selectColumn: (state, { payload }) => {
      const { id, type } = payload
      if (state.orderedColumns.some((col) => col.id === id && col.type === type)) return

      state.orderedColumns = [...state.orderedColumns, payload]
    },
    setCategories: (state, { payload }) => {
      state.categories = payload
    },
    setName: (state, { payload }) => {
      state.name = payload
    },
    setShared: (state, { payload }) => {
      state.shared = payload
    },
    setSubmissionColumnOptions: (state, { payload }) => {
      state.submissionColumnOptions = payload.map((option) => ({ ...option, type: SUBMISSION_TYPE }))
    },
    setTaskColumnOptions: (state, { payload }) => {
      state.taskColumnOptions = payload.map((option) => ({ ...option, type: TASK_TYPE }))
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(selectCategory.fulfilled, (state, action) => {
        const previousCategorySlug = state.selectedCategorySlug

        Object.assign(state, action.payload)

        if (action.payload.selectedCategorySlug !== previousCategorySlug) {
          taskReportBuilderSlice.caseReducers.deselectAllQuestionColumns(state)
        }
      })
      .addCase(selectCategory.rejected, (_state, action) => {
        errorToast(action.error.message)
      })
      .addCase(selectTaskForm.fulfilled, (state, action) => {
        const hadPreviousTaskForm = !!state.selectedTaskFormSlug

        Object.assign(state, action.payload)

        if (!hadPreviousTaskForm) {
          taskReportBuilderSlice.caseReducers.selectAllSubmissionDefaultColumns(state)
          taskReportBuilderSlice.caseReducers.selectAllTaskDefaultColumns(state)
        }

        taskReportBuilderSlice.caseReducers.deselectAllQuestionColumns(state)
        taskReportBuilderSlice.caseReducers.selectAllQuestionColumns(state)
      })
      .addCase(selectTaskForm.rejected, (_state, action) => {
        errorToast(action.error.message)
      })
      .addCase(loadTaskReportDataAsync.pending, (state) => {
        state.status = LOADING
      })
      .addCase(loadTaskReportDataAsync.fulfilled, (state, action) => {
        const { payload } = action

        state.categories = payload.categories
        state.name = payload.name
        state.shared = payload.shared
        state.selectedCategorySlug = payload.selectedCategorySlug
        state.selectedTaskFormSlug = payload.selectedTaskFormSlug
        state.submissionColumnOptions = payload.submissionColumnOptions.map((option) => (
          { ...option, type: SUBMISSION_TYPE }
        ))
        state.taskColumnOptions = payload.taskColumnOptions.map((option) => ({ ...option, type: TASK_TYPE }))

        mapOrderedColumnLabels(state, payload)

        state.status = LOADED
      })
      .addCase(loadTaskReportDataAsync.rejected, (state, action) => {
        errorToast(action.payload)
        state.status = LOADED
      })
  },
})

export const {
  deselectAllQuestionColumns,
  deselectAllSubmissionDefaultColumns,
  deselectAllTaskDefaultColumns,
  deselectColumn,
  moveColumn,
  selectAllQuestionColumns,
  selectAllSubmissionDefaultColumns,
  selectAllTaskDefaultColumns,
  selectColumn,
  setCategories,
  setName,
  setShared,
  setSubmissionColumnOptions,
  setTaskColumnOptions,
} = taskReportBuilderSlice.actions

export const categories = (state) => state[SLICE_NAME].categories

export const formQuestions = (state) => state[SLICE_NAME].formQuestions

export const isLoading = (state) => state[SLICE_NAME].status === LOADING

export const name = (state) => state[SLICE_NAME].name

export const orderedColumns = (state) => state[SLICE_NAME].orderedColumns

export const selectedCategorySlug = (state) => state[SLICE_NAME].selectedCategorySlug

export const selectedTaskFormSlug = (state) => state[SLICE_NAME].selectedTaskFormSlug

export const shared = (state) => state[SLICE_NAME].shared

export const submissionColumnOptions = (state) => state[SLICE_NAME].submissionColumnOptions

export const taskColumnOptions = (state) => state[SLICE_NAME].taskColumnOptions

export const taskForms = (state) => state[SLICE_NAME].taskForms

export const isMissingRequiredTaskReportBuilderData = (state) => (
  !selectedCategorySlug(state) || !selectedTaskFormSlug(state)
)

export default taskReportBuilderSlice.reducer
